import { action, computed, makeObservable, observable } from 'mobx';
import { Industry, TeamMemberRoleInput } from '@clipr/lib';

import { CatalogState } from '../../components/layout/catalogState';
import { DeleteTeamWindowState } from '../../components/teams/deleteTeamWindowState';
import { TeamProductWindowState } from '../../components/teams/teamProductWindowState';
import { AsyncResult } from '../../core';
import { Error, pageError } from '../../core/error';
import { Team } from '../../entities';
import { AdminTeamsCatalogSource } from '../../entities/adminTeamsCatalogSource';
import { Message, StoreNode } from '../../store';
import { Store } from '../../store/store';
import { notifyError } from '../../services/notifications';
import { isAgencyAccessRefused } from '../../api/apiErrorUtils';
import { parseTradeShowLeadsCsv, TradeShowLeadCsvRow } from '../agencySignupPage/agencyCsv';

export type AdminAgencyOption = {
  orgId: string;
  agencyName: string;
};

export type AdminTeamsPageParams = {
};

/** Controller for AdminTeamsPage. */
export class AdminTeamsPageState
  extends StoreNode {

  constructor(store: Store) {
    super(store);
    makeObservable<this, 'isAccessRefused' | 'setError' | 'setAccessRefused' | 'setLoaded'>(this, {
      isLoading: observable,
      error: observable,
      isTradeShowCsvUploading: observable,
      tradeShowInviteMessage: observable,
      tradeShowCsvStatus: observable,
      tradeShowCsvErrors: observable,
      agencies: observable,
      isAccessRefused: observable,
      assigningLibraryId: observable,
      showNoAccess: computed,
      pageMounted: action,
      handlePreviewScopeChanged: action,
      pageUnmounted: action,
      openTeamProductUpdate: action,
      refreshTeams: action,
      refreshAgencies: action,
      assignLibraryToAgency: action,
      setEnterpriseBilling: action,
      updateTradeShowInviteMessage: action,
      hasTradeShowInviteMessage: computed,
      canUploadTradeShowLeadsCsv: computed,
      importTradeShowLeadsCsv: action,
      reset: action,
      setError: action,
      setAccessRefused: action,
      setLoaded: action,
    });

    this.teamCatalogSource = new AdminTeamsCatalogSource(this.store, {
      pageSize: 20
    });
    this.teamCatalog = new CatalogState(this.store, {
      syncStatus: () => this.teamCatalogSource.syncStatus,
      isEndOfList: () => this.teamCatalogSource.isEndOfList
    });
    this.teamCatalog.listen((msg) => {
      switch (msg.type) {
        case 'scrollToBottom':
        case 'loadMore':
          this.teamCatalogSource.fetchMore();
          break;
      }
    });
  }

  isLoading: boolean = false;
  error: Error | null = null;
  isTradeShowCsvUploading = false;
  tradeShowInviteMessage = '';
  tradeShowCsvStatus: string | null = null;
  tradeShowCsvErrors: string[] = [];
  agencies: AdminAgencyOption[] = [];
  private isAccessRefused = false;
  assigningLibraryId: string | null = null;
  // The search index used by getTeams can lag successful assignments.
  readonly assignedAgencyIds = observable.map<string, string>();

  readonly teamCatalogSource: AdminTeamsCatalogSource;
  readonly teamCatalog = new CatalogState(this.store);

  /** An operator whose grants are gone sees no libraries, not a permissions error. */
  get showNoAccess() {
    const { user } = this.store;
    return this.isAccessRefused
      || (!!user?.roles.includes('Operator') && !user.hasPermission('AdminGetTeams'));
  }

  async pageMounted(_params: AdminTeamsPageParams) {
    const { deleteTeamWindow } = this.store;

    deleteTeamWindow.listen(
      this.deleteTeamWindowListener)

    this.reset();
    // Without the permission the page renders no data, so don't ask the backend for it.
    if (!this.store.user?.hasPermission('AdminGetTeams'))
      return;

    this.isLoading = true;

    const requests: AsyncResult[] = [
      this.teamCatalogSource.fetch(),
      this.teamCatalogSource.fetchTeamNamespaces()
    ];

    if (this.store.user?.hasPermission('AdminManageAgencies'))
      requests.push(this.refreshAgencies());

    const results = await Promise.all(requests);
    const error = results.map(([, error]) => error).find(Boolean);

    if (isAgencyAccessRefused(error))
      return this.setAccessRefused();

    if (error)
      return this.setError(error);

    this.setLoaded();
  }

  private deleteTeamWindowListener = action((msg: Message<DeleteTeamWindowState>) => {
    switch (msg.type) {
      case 'team:deleted':
        this.refreshTeams();
        break;
    }
  });

  // Preview start/end clears adminTeamLookup, which the catalog items read from (QA BUG-023).
  handlePreviewScopeChanged() {
    const isShowing = this.teamCatalogSource.items.length > 0;
    this.teamCatalogSource.items.clear();
    this.teamCatalogSource.pageInfo = null;
    if (isShowing)
      void this.refreshTeams();
  }

  pageUnmounted() {
    this.reset();

    this.store.deleteTeamWindow.unlisten(
      this.deleteTeamWindowListener);
  }

  openTeamProductUpdate(team: Team) {
    const window = this.store.teamProductWindow;
    const listener = (msg: Message<TeamProductWindowState>) => {
      switch (msg.type) {
        case 'teamUpdated':
          this.refreshTeams();
          window.unlisten(listener);
          break;
        case 'close':
          window.unlisten(listener);
          break;
      }
    }
    window.listen(listener);
    window.open({ team })
  }

  // #region State helpers
  async refreshTeams() {
    const requests: AsyncResult[] = [
      this.teamCatalogSource.fetch(),
      this.teamCatalogSource.fetchTeamNamespaces()
    ];

    return await Promise.all(requests);
  }

  async refreshAgencies(): AsyncResult<AdminAgencyOption[]> {
    const [listed, listError] = await this.store.apiService.adminListAgencies({ first: 200 });
    if (listError) return [null, listError];

    const [withoutLibraries, withoutLibrariesError] = await this.store.apiService.adminListAgenciesWithoutLibraries();
    if (withoutLibrariesError) return [null, withoutLibrariesError];

    const agencies = new Map<string, AdminAgencyOption>();
    for (const agency of withoutLibraries?.adminListAgenciesWithoutLibraries ?? []) {
      agencies.set(agency.orgId, { orgId: agency.orgId, agencyName: agency.agencyName });
    }
    for (const edge of listed?.adminListAgencies.edges ?? []) {
      const agency = edge.node;
      agencies.set(agency.orgId, { orgId: agency.orgId, agencyName: agency.agencyName });
    }

    this.agencies = [...agencies.values()].sort((a, b) => a.agencyName.localeCompare(b.agencyName));
    return [this.agencies];
  }

  async assignLibraryToAgency(teamId: string, agencyId: string) {
    const team = this.teamCatalogSource.entities.find(item => item.id === teamId);
    if (this.assigningLibraryId || !agencyId || team?.isAgencyAssigned || this.assignedAgencyIds.has(teamId)) return;

    this.assigningLibraryId = teamId;
    const [result, error] = await this.store.apiService.adminAssignLibraryToAgency({ teamId, agencyId });

    if (this.assigningLibraryId !== teamId) return;

    this.assigningLibraryId = null;
    const assignedAgencyId = result?.adminAssignLibraryToAgency?.organizationId;
    if (error || !assignedAgencyId) {
      notifyError(this, `Could not assign ${team?.name ?? 'this library'}: ${error?.message ?? 'Please try again.'}`);
      return;
    }

    this.assignedAgencyIds.set(teamId, assignedAgencyId);
    const [, refreshError] = await this.teamCatalogSource.fetch();
    if (refreshError)
      notifyError(this, `${team?.name ?? 'Library'} assigned, but could not refresh the list.`);
  }

  async setEnterpriseBilling(orgId: string, enabled: boolean) {
    const [, error] = await this.store.apiService.adminSetOrgEnterpriseBilling({ orgId, enabled });
    if (error) {
      notifyError(this, `Could not update Enterprise: ${error.message}`);
      return;
    }

    const [, refreshError] = await this.teamCatalogSource.fetch();
    if (refreshError)
      notifyError(this, 'Enterprise updated, but could not refresh the list.');
  }

  updateTradeShowInviteMessage(value: string) {
    this.tradeShowInviteMessage = value;
  }

  get hasTradeShowInviteMessage() {
    return this.tradeShowInviteMessage.trim().length > 0;
  }

  get canUploadTradeShowLeadsCsv() {
    return !this.isTradeShowCsvUploading && this.hasTradeShowInviteMessage;
  }

  async importTradeShowLeadsCsv(data: string[][]) {
    this.tradeShowCsvStatus = null;
    this.tradeShowCsvErrors = [];

    if (!this.hasTradeShowInviteMessage) {
      this.tradeShowCsvErrors = ['Invite message is required before uploading leads.'];
      return;
    }

    const parsed = parseTradeShowLeadsCsv(data);
    this.tradeShowCsvErrors = parsed.errors;

    if (parsed.rows.length === 0) {
      if (this.tradeShowCsvErrors.length === 0)
        this.tradeShowCsvErrors = ['No leads were found in the CSV.'];
      return;
    }

    this.isTradeShowCsvUploading = true;
    let successCount = 0;

    for (const lead of parsed.rows) {
      const error = await this.createLibraryForTradeShowLead(lead);
      if (error) {
        this.tradeShowCsvErrors = [...this.tradeShowCsvErrors, `${lead.email}: ${error}`];
      } else {
        successCount += 1;
      }
    }

    this.isTradeShowCsvUploading = false;
    this.tradeShowCsvStatus = `${successCount} lead${successCount === 1 ? '' : 's'} imported.`;

    if (successCount > 0)
      await this.refreshTeams();
  }

  reset() {
    this.isLoading = false;
    this.error = null;
    this.isAccessRefused = false;
    this.isTradeShowCsvUploading = false;
    this.tradeShowInviteMessage = '';
    this.tradeShowCsvStatus = null;
    this.tradeShowCsvErrors = [];
    this.agencies = [];
    this.assigningLibraryId = null;
    this.assignedAgencyIds.clear();
    this.teamCatalogSource.searchFilter = null;
    this.teamCatalogSource.searchBarModel.clear();
    this.teamCatalogSource.items.clear();
    this.teamCatalogSource.pageInfo = null;
  }

  private setError(error?: Error) {
    if (!error)
      error = pageError();

    this.isLoading = false;
    this.error = error;
  }

  private setAccessRefused() {
    this.isLoading = false;
    this.isAccessRefused = true;
    void this.store.agencyOperatorAccess.handleAccessRefused();
  }

  private setLoaded() {
    this.isLoading = false;
    this.error = null;
  }
  // #endregion

  private async createLibraryForTradeShowLead(lead: TradeShowLeadCsvRow): Promise<string | null> {
    const libraryName = `${lead.fullName} ${lead.agency} - ${lead.email}`;
    const description = [lead.rank, lead.fullName, lead.email].filter(Boolean).join(' - ');
    const message = this.tradeShowInviteMessage.trim();
    const [teamRes, teamErr] = await this.store.apiService.createTeam({
      args: {
        name: libraryName,
        displayName: libraryName,
        description,
        externalEmail: lead.email,
        // Leads are police. Industry keeps the library public safety while the report follows
        // each invitee's own default rather than a library-wide speciality.
        industry: Industry.PublicSafety,
      },
    });

    if (teamErr)
      return teamErr.message;

    const teamId = teamRes?.createTeam.id;
    if (!teamId)
      return 'Library was not created.';

    const [, inviteErr] = await this.store.apiService.inviteTeamMember({
      teamId,
      email: lead.email,
      role: TeamMemberRoleInput.Admin,
      message: message || undefined,
    });

    return inviteErr?.message ?? null;
  }
}
